import { useMemo, useState } from "react";
import { Pencil, Plus, X } from "lucide-react";

import CustomDocumentTile from "@/components/application/merchant-tasks/request-docs/CustomDocumentTile";
import DocumentTile from "@/components/application/merchant-tasks/request-docs/DocumentTile";
import type { DocumentCatalog } from "@/components/application/merchant-tasks/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import type { FlowCardPayload, FlowEdits, FlowRequestedDocument } from "./payload";

/** The documents as a press names them: catalog keys, then the "Other" documents by name. */
function requestNames(keys: Set<string>, custom: string[]): string[] {
  return [...keys, ...custom.map((name) => name.trim()).filter(Boolean)];
}

function initialSelection(requested: FlowRequestedDocument[]) {
  return {
    keys: new Set(requested.filter((doc) => !doc.custom).map((doc) => doc.key)),
    custom: requested.filter((doc) => doc.custom).map((doc) => doc.label),
  };
}

export interface MessageEditorProps {
  message: FlowCardPayload["message"];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  readOnly: boolean;
}

/** What the merchant reads with the task; "" sends the recipe's standard one. */
export function MessageEditor({ message, value, onChange, readOnly }: MessageEditorProps) {
  const [editing, setEditing] = useState(false);
  const text = value ?? message.value;
  if (editing && !readOnly) {
    return (
      <div className="space-y-1">
        <Textarea
          autoFocus
          aria-label="Message"
          rows={3}
          value={text}
          placeholder={message.default}
          className="text-xs"
          onChange={(event) =>
            onChange(event.target.value === message.value ? undefined : event.target.value)
          }
          onBlur={() => setEditing(false)}
        />
        <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
          Message
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-0.5">
      <p className="flex items-start gap-1.5 text-xs text-foreground [overflow-wrap:anywhere]">
        <span className="whitespace-pre-wrap">{text || message.default}</span>
        {message.editable && !readOnly ? (
          <button
            type="button"
            aria-label="Edit message"
            className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-3" />
          </button>
        ) : null}
      </p>
      <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
        Message
      </p>
    </div>
  );
}

export interface FlowDocumentsBodyProps {
  card: FlowCardPayload;
  edits: FlowEdits;
  onEdits: (next: Partial<FlowEdits>) => void;
  readOnly: boolean;
}

/**
 * A document request's body: the message (editable), then "Add documents", which opens the app's
 * Request Docs tiles (`DocumentTile`, `CustomDocumentTile`) over the card's own catalog. The
 * requested list is what the stepper shows; a change goes back as the press's `documents`.
 */
export function FlowDocumentsBody({ card, edits, onEdits, readOnly }: FlowDocumentsBodyProps) {
  const documents = card.documents;
  const [open, setOpen] = useState(false);
  const start = useMemo(() => initialSelection(documents?.requested ?? []), [documents]);
  const [keys, setKeys] = useState(start.keys);
  const [custom, setCustom] = useState(start.custom);
  if (!documents) return null;
  const catalog: DocumentCatalog = documents.catalog;

  // The press drops a list back as the card's (`flowOverrides`).
  const commit = (nextKeys: Set<string>, nextCustom: string[]) => {
    setKeys(nextKeys);
    setCustom(nextCustom);
    onEdits({ documents: requestNames(nextKeys, nextCustom) });
  };
  const toggle = (key: string) => {
    const next = new Set(keys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    commit(next, custom);
  };

  return (
    <div className="space-y-2">
      <MessageEditor
        message={card.message}
        value={edits.message}
        readOnly={readOnly || !card.overrides.includes("message")}
        onChange={(message) => onEdits({ message })}
      />
      {custom.length > 0 ? (
        <ul aria-label="Other documents" className="space-y-1">
          {custom.map((name, index) => (
            <li key={index} className="flex items-center gap-1.5">
              <Input
                aria-label="Document name"
                value={name}
                maxLength={catalog.max_custom_label_length}
                disabled={readOnly}
                placeholder="Name the document"
                className="h-7 text-xs"
                onChange={(event) =>
                  commit(
                    keys,
                    custom.map((row, i) => (i === index ? event.target.value : row))
                  )
                }
              />
              {!readOnly ? (
                <button
                  type="button"
                  aria-label={`Remove ${name || "document"}`}
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() =>
                    commit(
                      keys,
                      custom.filter((_, i) => i !== index)
                    )
                  }
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {readOnly ? null : open ? (
        <div className="grid grid-cols-3 gap-2 border-t pt-2 max-sm:grid-cols-2">
          {catalog.document_types.map((entry) =>
            entry.custom_label ? (
              <CustomDocumentTile
                key={entry.key}
                entry={entry}
                count={custom.length}
                disabled={custom.length >= catalog.max_custom_documents}
                onAdd={() => commit(keys, [...custom, ""])}
                compact
              />
            ) : (
              <DocumentTile
                key={entry.key}
                entry={entry}
                checked={keys.has(entry.key)}
                disabled={false}
                onToggle={toggle}
                compact
              />
            )
          )}
        </div>
      ) : (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto gap-1 border-t px-0 pt-2 text-xs"
          onClick={() => setOpen(true)}
        >
          <Plus className="size-3.5" />
          Add documents
        </Button>
      )}
    </div>
  );
}
